import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { 
    EyeIcon, 
    EyeSlashIcon, 
    CameraIcon,
    BuildingOfficeIcon,
    EnvelopeIcon,
    PhoneIcon,
    KeyIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import axios from '../../axios';
import Loader from '../../Layout/Loaders/Loader';
import { UserContext } from '../../UserContext';
import notify from '../../Utils/notify';
import { Button } from '../UI';

const defaultImg = "/images/default-user.png";

const Perfil = () => {
    const { user, setSucursal } = useContext(UserContext);
    const [sucursales, setSucursales] = useState([]);
    const [sucursalSelected, setSucursalSelected] = useState('');
    const [showDetail, setShowDetail] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);

    const { token } = useParams();
    const [data, setData] = useState({ 
        password: '', 
        password_confirmation: '', 
        token: token, 
        email: '' 
    });
    const [loader, setLoader] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoader(true);
        setLoadingButton(true);

        if (data.password === '' || data.password_confirmation === '' || data.email === '') {
            notify.error('Todos los campos son obligatorios.');
            setLoader(false);
            setLoadingButton(false);
            return;
        }

        if (data.password !== data.password_confirmation) {
            notify.error('Las contraseñas no coinciden. Verifica que sean iguales.');
            setLoader(false);
            setLoadingButton(false);
            return;
        }

        if (data.password.length < 8) {
            notify.warning('La contraseña debe tener al menos 8 caracteres.');
            setLoader(false);
            setLoadingButton(false);
            return;
        }

        try {
            const res = await axios.post('/api/change_password', {
                email: data.email,
                password: data.password,
                password_confirmation: data.password_confirmation
            });
            notify.success(res.data.message || 'Contraseña actualizada exitosamente');
            setData({ password: '', password_confirmation: '', token: token, email: user.email });
            setShowDetail(false);
        } catch (e) {
            console.log("Error al cambiar contraseña:", e);
            const mensaje = e.response?.data?.message || "Error al cambiar la contraseña";
            notify.error(mensaje);
        } finally {
            setLoader(false);
            setLoadingButton(false);
        }
    };

    useEffect(() => {
        if (user.id) {
            console.log("Usuario completo:", user);
            console.log("Sucursales del usuario:", user.sucursals);
            
            // Cargar sucursales primero
            if (user.sucursals?.length > 0) {
                setSucursales(user.sucursals);
                console.log("Sucursales establecidas:", user.sucursals);
                
                // Luego manejar la selección
                const storedSucursal = localStorage.getItem('sucursal_selected');
                if (storedSucursal !== null) {
                    setSucursalSelected(storedSucursal);
                } else {
                    const firstSucursalId = String(user.sucursals[0].id);
                    setSucursalSelected(firstSucursalId);
                    localStorage.setItem('sucursal_selected', firstSucursalId);
                    localStorage.setItem('sucursal_name', user.sucursals[0].nombre_sucursal);
                    setSucursal(user.sucursals[0].nombre_sucursal);
                }
            } else {
                console.log("No hay sucursales en el usuario");
            }
            
            // Establecer el email del usuario para el cambio de contraseña
            setData(prev => ({ ...prev, email: user.email }));
        }
    }, [user, setSucursal]);

    const _updateImg = async (file) => {
        if (!file) return;
        
        // Validar tipo de archivo
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            notify.error('Solo se permiten imágenes PNG, JPG o GIF');
            return;
        }

        // Validar tamaño (máx 5MB)
        if (file.size > 5 * 1024 * 1024) {
            notify.error('La imagen no debe superar los 5MB');
            return;
        }

        setUploadingImage(true);
        const fData = new FormData();
        fData.append('id', user.id);
        fData.append("file", file);

        try {
            const resp = await axios.post('api/update-profile-image', fData);
            notify.success(resp.data.message || 'Imagen actualizada exitosamente');
            // Recargar la página para actualizar la imagen en el contexto
            setTimeout(() => window.location.reload(), 1000);
        } catch (e) {
            const mensaje = e.response?.data?.message || 'Error al actualizar la imagen';
            notify.error(mensaje);
        } finally {
            setUploadingImage(false);
        }
    };

    const changeActiveSucursal = (e) => {
        setSucursalSelected(e.target.value);
        const sucName = e.target.options[e.target.selectedIndex].text;
        setSucursal(sucName);
        localStorage.setItem('sucursal_selected', e.target.value);
        localStorage.setItem('sucursal_name', sucName);
        notify.success(`Sucursal cambiada a: ${sucName}`);
    };

    const handleToggle = () => setShowDetail(!showDetail);

    return (
        
        <div>
            <div className='container-admin-menu'>
                <Breadcrumb actual={user.name || "Mi perfil"} />
            </div>
            
            <div className='container-admin-container' >
                {user.id ? (
                    <div className='bg-white rounded-lg shadow-sm'>
                        {/* Header */}
                        <div className="border-b border-gray-200 px-4 py-6 info-badge">
                            <h2 className="text-2xl font-semibold text-white">Mi Perfil</h2>
                        </div>

                        {/* Contenido Principal */}
                        <div className="p-6">
                            {/* Sección de Información Personal */}
                            <div className="bg-gray-50 rounded-lg p-6 mb-6">
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                    {/* Imagen de Perfil */}
                                    <div className="lg:col-span-3 flex flex-col items-center">
                                        <div className="relative group">
                                            <img 
                                                src={user.image || defaultImg} 
                                                alt={user.name ? `Foto de perfil de ${user.name}` : "Foto de perfil"} 
                                                className="w-40 h-40 rounded-lg object-cover shadow-md"
                                            />
                                            <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                                {uploadingImage ? (
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                                ) : (
                                                    <CameraIcon className="w-8 h-8 text-white" />
                                                )}
                                                <input 
                                                    type="file" 
                                                    className="hidden" 
                                                    onChange={(e) => _updateImg(e.target.files[0])} 
                                                    accept="image/png, image/gif, image/jpeg, image/jpg"
                                                    disabled={uploadingImage}
                                                />
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2 text-center">
                                            Click para cambiar
                                        </p>
                                    </div>

                                    {/* Información del Usuario */}
                                    <div className="lg:col-span-5 space-y-4">
                                        <div>
                                            <label className="text-sm font-medium text-gray-500">ID</label>
                                            <p className="text-lg font-semibold text-gray-800">{user.id}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-500">Nombre</label>
                                            <p className="text-lg font-semibold text-gray-800">{user.name}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-500">Rol</label>
                                            {user.user_roles?.length > 0 ? (
                                                user.user_roles.map((role, index) => (
                                                    <p key={index} className="text-lg font-semibold capitalize text-gray-800">
                                                        {role}
                                                    </p>
                                                ))
                                            ) : (
                                                <p className="text-lg font-semibold text-gray-800">Sin rol asignado</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-500">Fecha de ingreso</label>
                                            <p className="text-lg font-semibold text-gray-800">
                                                {new Date(user.created_at).toLocaleDateString("es-MX", { 
                                                    year: 'numeric', 
                                                    month: 'long', 
                                                    day: 'numeric' 
                                                })}
                                            </p>
                                        </div>

                                        {/* Cambiar Sucursal */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Cambiar de Sucursal
                                            </label>
                                            {sucursales?.length > 0 ? (
                                                <select 
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all browser-default"
                                                    value={sucursalSelected}
                                                    onChange={changeActiveSucursal}
                                                >
                                                    <option value='' disabled>Selecciona una sucursal</option>
                                                    {sucursales.map((suc, i) => (
                                                        <option key={i} value={suc.id}>
                                                            {suc.nombre_sucursal}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <p className="text-sm text-gray-500">Cargando sucursales...</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Sucursales del Usuario */}
                                    <div className="lg:col-span-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">
                                            Mis Sucursales
                                        </label>
                                        <div className="space-y-2">
                                            {sucursales?.map((suc, i) => (
                                                <div 
                                                    key={i} 
                                                    className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm"
                                                >
                                                    <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                                                    <span className="text-sm text-gray-700">{suc.nombre_sucursal}</span>
                                                    {suc.id == sucursalSelected && (
                                                        <CheckCircleIcon className="w-4 h-4 text-green-500 ml-auto" />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Información de Contacto */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email
                                    </label>
                                    <div className="flex items-center gap-3 px-4 py-3 border border-gray-300 rounded-lg bg-white">
                                        <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                                        <span className="text-gray-700">{user.email || 'No especificado'}</span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Teléfono
                                    </label>
                                    <div className="flex items-center gap-3 px-4 py-3 border border-gray-300 rounded-lg bg-white">
                                        <PhoneIcon className="w-5 h-5 text-gray-400" />
                                        <span className="text-gray-700">{user.phone || 'No especificado'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Botón Cambiar Contraseña */}
                            <div className="flex items-center justify-center py-6 border-t border-gray-200">
                                <Button
                                    variant={showDetail ? 'secondary' : 'primary'}
                                    onClick={handleToggle}
                                    icon={KeyIcon}
                                    className="shadow-sm"
                                >
                                    {showDetail ? 'Cancelar' : 'Cambiar Contraseña'}
                                </Button>
                            </div>

                            {/* Formulario de Cambio de Contraseña */}
                            <Transition
                                show={showDetail}
                                enter="transition-all duration-300 ease-out"
                                enterFrom="opacity-0 max-h-0"
                                enterTo="opacity-100 max-h-[1000px]"
                                leave="transition-all duration-200 ease-in"
                                leaveFrom="opacity-100 max-h-[1000px]"
                                leaveTo="opacity-0 max-h-0"
                            >
                                <div className="overflow-hidden">
                                    <form onSubmit={handleSubmit} className="space-y-6 pt-6 border-t border-gray-200">
                                        <div className="bg-blue-50 rounded-lg p-6">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="bg-blue-100 p-2 rounded-lg">
                                                    <KeyIcon className="w-6 h-6 text-blue-600" />
                                                </div>
                                                <h3 className="text-xl font-semibold text-gray-800">
                                                    Actualizar Contraseña
                                                </h3>
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                Te sugerimos algo fácil de recordar, pero no la compartas con nadie.
                                            </p>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-6 space-y-6">
                                            {/* Email (oculto para el cambio de contraseña) */}
                                            <input
                                                type="hidden"
                                                value={user.email}
                                                onChange={(e) => setData({ ...data, email: e.target.value })}
                                            />

                                            {/* Nueva Contraseña */}
                                            <div>
                                                <label 
                                                    htmlFor="newpassword" 
                                                    className="block text-sm font-medium text-gray-700 mb-2"
                                                >
                                                    Nueva Contraseña
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={showPassword ? 'text' : 'password'}
                                                        id="newpassword"
                                                        className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        placeholder="Ingresa tu nueva contraseña"
                                                        value={data.password}
                                                        onChange={(e) => setData({ ...data, password: e.target.value })}
                                                        minLength={8}
                                                        required
                                                    />
                                                    <button
                                                        type="button"
                                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                    >
                                                        {showPassword ? (
                                                            <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                                        ) : (
                                                            <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                                        )}
                                                    </button>
                                                </div>
                                                <p className="mt-1 text-xs text-gray-500">
                                                    Mínimo 8 caracteres
                                                </p>
                                            </div>

                                            {/* Confirmar Contraseña */}
                                            <div>
                                                <label 
                                                    htmlFor="confirmpassword" 
                                                    className="block text-sm font-medium text-gray-700 mb-2"
                                                >
                                                    Confirmar Nueva Contraseña
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={showPasswordConfirmation ? 'text' : 'password'}
                                                        id="confirmpassword"
                                                        className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        placeholder="Repite tu nueva contraseña"
                                                        value={data.password_confirmation}
                                                        onChange={(e) => setData({ ...data, password_confirmation: e.target.value })}
                                                        minLength={8}
                                                        required
                                                    />
                                                    <button
                                                        type="button"
                                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                        onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                                                    >
                                                        {showPasswordConfirmation ? (
                                                            <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                                        ) : (
                                                            <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Botones de Acción */}
                                        <div className="flex justify-end gap-3">
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                onClick={handleToggle}
                                            >
                                                Cancelar
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                className="cursor-pointer"
                                                loading={loadingButton}
                                                disabled={loadingButton}
                                            >
                                                {loadingButton ? 'Actualizando...' : 'Cambiar Contraseña'}
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            </Transition>
                        </div>
                    </div>
                ) : (
                    <div className='bg-white rounded-lg shadow-sm p-8'>
                        <Loader />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Perfil;